import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import { sendCampaignInviteReminderById, sendCampaignInviteReminders } from 'api/campaigns';
import * as Tooltip from 'components/tooltip';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useForceRender } from 'customHooks/useForceRender';
import { dateFromServer } from 'helpers/dates';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { queryClient } from 'queries/queryClient';
import { CampaignMemberInvite } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface InviteReminderDialogProps extends AlertDialog.AlertDialogRootProps {
    invite?: CampaignMemberInvite;
}

const InviteReminderDialog = (props: InviteReminderDialogProps) => {
    const { error, success } = useToast();
    const { children, invite, ...rest } = props;
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign.id;
    const { key, forceRender } = useForceRender();
    const inviteId = invite?.id;

    const { isPending: isSendingReminders, mutate: sendReminders } = useMutation({
        mutationFn: () => sendCampaignInviteReminders(campaignId),
        onSuccess: () => {
            success('All invites successfully resent!');
            queryClient.invalidateQueries({ queryKey: campaignQueryKeys.invites(campaignId) });
            forceRender();
        },
        onError: (e: GivebutterAxiosError) =>
            error(e.response?.data?.message ?? 'Failed to resend all invites'),
    });

    const { isPending: isSendingReminder, mutate: sendReminderById } = useMutation({
        mutationFn: (inviteId: string) => sendCampaignInviteReminderById(campaignId, inviteId),
        onSuccess: () => {
            success('Invite reminder resent!');
            queryClient.invalidateQueries({ queryKey: campaignQueryKeys.invites(campaignId) });
            forceRender();
        },
        onError: (e: GivebutterAxiosError) =>
            error(e.response?.data?.message ?? 'Failed to resend invite reminder'),
    });

    const handleSendReminders = () => {
        if (inviteId) {
            sendReminderById(inviteId);
        } else {
            sendReminders();
        }
    };

    const isPending = isSendingReminders || isSendingReminder;
    const canResend =
        invite?.last_reminded_at !== undefined ? canResendInvite(invite.last_reminded_at) : true;
    const isDisabled = isPending || !canResend;
    const description = inviteId ? 'Resend invite' : 'Send reminders';

    return (
        <AlertDialog.Root {...rest} key={key} type="warning">
            <AlertDialog.Trigger asChild={true}>
                <div className="flex justify-end">
                    {!canResend ? (
                        <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                                <Button variant={'outline'} color={'neutral'} disabled={isDisabled}>
                                    {isPending && inviteId && <Loader size="sm" variant="static" />}
                                    {description}
                                </Button>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content inverse>
                                    <p>You can resend after 24 hours</p>
                                    <Tooltip.Arrow inverse />
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    ) : (
                        <Button variant={'outline'} color={'neutral'} disabled={isDisabled}>
                            {isPending && inviteId && <Loader size="sm" variant="static" />}
                            {description}
                        </Button>
                    )}
                </div>
            </AlertDialog.Trigger>
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>{description}</AlertDialog.Title>
                        <AlertDialog.Description>
                            We'll automatically prevent members from receiving more than one
                            reminder per 24-hour period. Please don't refresh or leave this page
                            until sending is complete, it may take a few seconds.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <Button
                            variant="solid"
                            disabled={isDisabled}
                            onClick={() => handleSendReminders()}
                            className="w-1/2"
                        >
                            {description}
                        </Button>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const canResendInvite = (lastRemindedAt: string | null): boolean => {
    if (!lastRemindedAt) return true;

    return dateFromServer(lastRemindedAt).diffNow('hours').hours <= -24;
};

const Root = InviteReminderDialog;

export { InviteReminderDialog, Root };
